/**
 * PSR-FOLLOWUP-IIA-INGEST — Image-observation sentiment fetcher.
 *
 * Fourth source for the Public Sentiment Engine, alongside GDELT /
 * MuniCode-Granicus-CivicPlus / Regulations.gov-FERC-ADAMS. Where the
 * other three reach OUT to the web, this one reaches IN to the user's
 * own evidence base: image-observation claims that IIA-2 / IIA-3
 * deposited on `siteContext.claims[]`.
 *
 * Why it matters:
 *  - Protest flyers + community-meeting handouts uploaded as images
 *    classify as `kind: 'other'` (no per-kind structured extractor) and
 *    land in `siteContext.claims[]` with `kind: 'image-observation'`
 *    via the IIA-3 free-text path. Without this source they would
 *    never reach the public-sentiment pipeline — even though they
 *    are the most direct public-sentiment signal available.
 *  - The IIA-1 invariant guarantees image bytes are released after
 *    extraction; only the JSON observation persists. This provider is
 *    text-only by design — it operates on the persisted `claim` /
 *    `sourceSpan` strings, never on the original image bytes.
 *
 * Approval gate (HARD):
 *  - Reads `siteContext.claims[]` filtered to
 *      `versionNote === 'iia-3:image-observation'` (the IIA-3 marker
 *      from iiaSiteContextBridge.ts) AND `state === 'authored'`
 *      (set by the IIA-5 per-image review card when the user clicks
 *      "approve").
 *  - Unapproved observations (the default `state: undefined`) are
 *    DROPPED. The bridge writes them with `userApproved: false` by
 *    contract; only the IIA-5 review surface promotes them.
 *  - `mis-extracted` claims are also dropped — the user explicitly
 *    flagged them as wrong; surfacing them as sentiment would
 *    contradict the soft-delete semantic.
 *
 * Classification:
 *  - BTM-tech bucket: `classifyBtmTech` from PSE-2's
 *    `sentimentPolarity.ts` — same vocabulary GDELT uses, so the
 *    aggregator buckets line up.
 *  - Polarity: Gemini Flash structured call. Image-observation text is
 *    short + emotional (flyer headlines, hand-out blurbs) — the
 *    keyword polarity scorer was tuned for council-minutes parliament-
 *    speak and underfires on this corpus. The structured call returns
 *    `{ sentimentScore, polarityRationale }` so the scoring is
 *    inspectable.
 *  - Confidence: capped at `IMAGE_OBSERVATION_CONFIDENCE_CAP` (0.50).
 *    Lower than GDELT's `lengthBasedConfidence` ceiling (which can
 *    reach 1.0 on a 2000-char body) because the underlying
 *    image-observation is already vision-confidence-capped at 0.55
 *    by IIA-3. Compounding the two ceilings gives us an honest top-end
 *    of ~0.50 — the cap codifies that math directly.
 *
 * Resilience: NEVER throws. Every failure path collapses into a
 * structured `{ status: 'unavailable', reason, entries: [] }` so the
 * orchestrator stays resilient. Empty claims → `{ status: 'empty',
 * entries: [] }` (the orchestrator already treats `empty` as a clean
 * no-signal pass-through; see `dispatchSourceTech`).
 */
import type { PublicSentimentEntry } from '../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../publicSentimentOrchestrator';
import { auth, db } from '../../firebase';
import { runGeminiJSON, Type, type Schema } from '../geminiClient';
import {
  IIA_OBSERVATION_VERSION_NOTE,
} from '../knowledge/iiaSiteContextBridge';
import {
  classifyBtmTech,
  truncateSnippet,
} from './sentimentPolarity';
import {
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../../lib/abortSignal';

/**
 * Confidence ceiling for image-observation entries.
 *
 * Floor of (IIA-3 vision-confidence cap 0.55) × (LLM polarity
 * confidence ≈ 0.90 at best for a short ungrounded snippet) ≈ 0.50.
 * Encoded directly here instead of computed so the cap shows up in
 * search + the audit pack table without needing to trace the
 * multiplication through two files.
 */
export const IMAGE_OBSERVATION_CONFIDENCE_CAP = 0.50;

/**
 * Default model. Mirrors PSE-2's pattern (each fetcher names its own
 * model so a future cost-tuning sweep can dial them independently).
 * Flash because the classification is two short floats — pro is
 * overkill.
 */
const DEFAULT_MODEL = 'gemini-2.5-flash';

/** Soft cap on the number of observations we send through the classifier
 *  in a single call. The orchestrator caps the per-source TOTAL entries
 *  (50 default / 200 standalone-psr), but a runaway siteContext with
 *  thousands of image-observation claims would still blow our cost
 *  budget without this inner cap. */
const MAX_OBSERVATIONS_PER_CALL = 200;

/* ── Source-shape ──────────────────────────────────────────────────── */

/**
 * Single image-observation claim shape we accept. Mirrors the subset of
 * `ExtractedClaim` we need — keeps this fetcher decoupled from the full
 * SiteContext type, and makes the unit test seam trivial (callers can
 * pass synthetic arrays without constructing a full SiteContext).
 */
export interface ImageObservationClaimInput {
  id: string;
  /** The full observation text from IIA-3 free-text fallback (≤120 chars
   *  after `applyIiaObservationsToSiteContext` slices it). */
  claim: string;
  /** Source-span snippet (≤240 chars; the IIA-3 observation prefix). */
  sourceSpan: string;
  /** SC-3 state. Only `'authored'` rows are eligible — see top-of-file. */
  state?: 'mis-extracted' | 'authored';
  /** PL3 versionNote — used to identify image-observation claims among
   *  the other claim families on `siteContext.claims[]`. */
  versionNote?: string;
  /** ISO timestamp from the bridge's `now` stamp. */
  lastWrittenAt?: string;
  /** SED doc the observation came from (audit trail; not used in the
   *  sentiment scoring path itself). */
  sourceDocId?: string;
}

/* ── Filter (eligibility) ──────────────────────────────────────────── */

/**
 * Pure filter — exported for the unit tests and for the orchestrator
 * pre-flight ("do we have anything to send?") so the cost-cap path
 * doesn't even fire when there are zero approved observations.
 */
export function filterEligibleImageObservations(
  claims: readonly ImageObservationClaimInput[] | null | undefined,
): ImageObservationClaimInput[] {
  if (!Array.isArray(claims) || claims.length === 0) return [];
  const out: ImageObservationClaimInput[] = [];
  for (const c of claims) {
    if (!c || typeof c !== 'object') continue;
    if (c.versionNote !== IIA_OBSERVATION_VERSION_NOTE) continue;
    if (c.state !== 'authored') continue;
    const text = (c.claim ?? c.sourceSpan ?? '').toString().trim();
    if (text.length === 0) continue;
    out.push(c);
  }
  return out;
}

/* ── Gemini polarity classifier ────────────────────────────────────── */

/**
 * Structured-output schema. Each input observation maps to one output
 * `{ id, sentimentScore, polarityRationale }` row — `id` carries the
 * caller-assigned key so we can re-associate the structured response
 * with the input even if the model reorders.
 */
const POLARITY_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    polarities: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          sentimentScore: { type: Type.NUMBER },
          polarityRationale: { type: Type.STRING },
        },
        required: ['id', 'sentimentScore'],
      },
    },
  },
  required: ['polarities'],
};

interface PolarityResponse {
  polarities?: Array<{
    id?: unknown;
    sentimentScore?: unknown;
    polarityRationale?: unknown;
  }>;
}

/**
 * Build the polarity-classification prompt. Each observation is rendered
 * as a numbered block tagged with its claim id; the model is asked to
 * emit a sentiment score in [-1, +1] per id and a one-sentence rationale.
 *
 * Prompt structure pinned by the unit tests — keep the leading line
 * stable so the test's `expect.stringContaining` pin survives prompt
 * polish.
 */
export function buildImageObservationPolarityPrompt(
  claims: readonly ImageObservationClaimInput[],
): string {
  const header = `You are classifying the polarity of short text excerpts extracted from images uploaded by a site assessment user. The text was extracted by vision-OCR; the original image bytes have been released. Each excerpt was tagged 'image-observation' (kind: 'other' classification — typically protest flyers, community meeting handouts, or signage). Do NOT speculate about the image; classify only the text shown.

For each excerpt, emit ONE row:
- id: the excerpt id verbatim.
- sentimentScore: float in [-1, +1]. +1 = strongly supportive of the proposed siting / project, 0 = neutral / no signal, -1 = strongly opposed. Calibration anchors: flyer "no data center" → -0.8, flyer "support SMR jobs" → +0.7, neutral notice → 0.0.
- polarityRationale: 1 sentence (≤120 chars) explaining the score.

EXCERPTS:`;

  const blocks = claims.map((c, i) => {
    const text = (c.claim ?? c.sourceSpan ?? '').toString().trim();
    return `${i + 1}. id="${c.id}"\n${text.slice(0, 800)}`;
  }).join('\n\n');

  return `${header}\n\n${blocks}`;
}

/**
 * Call the structured Gemini polarity classifier and return a map from
 * claim id → sentimentScore (clamped to [-1, +1]).
 *
 * NEVER throws — every failure path returns an empty map so the caller
 * can fall back to neutral-zero scoring for any unmapped id.
 */
export async function classifyImageObservationPolarities(
  claims: readonly ImageObservationClaimInput[],
  options: {
    runner?: typeof runGeminiJSON;
    model?: string;
    timeoutMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<Map<string, number>> {
  throwIfAborted(options.signal);
  if (claims.length === 0) return new Map();
  const runner = options.runner ?? runGeminiJSON;
  const prompt = buildImageObservationPolarityPrompt(claims);
  let parsed: PolarityResponse;
  try {
    parsed = await runner<PolarityResponse>({
      model: options.model ?? DEFAULT_MODEL,
      contents: prompt,
      schema: POLARITY_SCHEMA,
      signal: options.signal,
      ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
    });
  } catch (e) {
    rethrowIfAborted(options.signal, e);
    console.warn('[imageObservationSentiment] gemini polarity classifier failed:', (e as Error)?.message);
    return new Map();
  }
  const out = new Map<string, number>();
  if (!parsed || typeof parsed !== 'object') return out;
  const rows = Array.isArray(parsed.polarities) ? parsed.polarities : [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    if (!id) continue;
    const raw = typeof r.sentimentScore === 'number' ? r.sentimentScore : NaN;
    // Strict Number.isFinite guard per HARD RULE — the structured-output
    // contract should never let NaN through, but the runtime hardening
    // is cheap and prevents a poisoned response from skewing the
    // aggregator's confidence-weighted mean.
    if (!Number.isFinite(raw)) continue;
    out.set(id, Math.max(-1, Math.min(1, raw)));
  }
  return out;
}

/* ── Site-claims Firestore loader ──────────────────────────────────── */

/**
 * Load `siteContext.claims[]` for `siteId` via the standard Firestore
 * client. Returns `null` (not throws) on any failure — missing site doc,
 * unauthenticated session, transport blip — so the fetcher can collapse
 * to `unavailable` cleanly.
 *
 * Path: `users/{uid}/sites/{siteId}` (the canonical site path used by
 * sedLoader.ts + cafService.ts). NO bypass / admin / service-account
 * read — Firestore rules already gate this to the site owner, which is
 * exactly the access we want.
 */
async function loadSiteClaimsForSentiment(
  siteId: string,
  signal?: AbortSignal,
): Promise<ImageObservationClaimInput[] | null> {
  throwIfAborted(signal);
  const user = auth.currentUser;
  if (!user || !siteId) return null;
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const ref = doc(db, 'users', user.uid, 'sites', siteId);
    const snap = await raceWithAbort(getDoc(ref), signal);
    throwIfAborted(signal);
    if (!snap.exists()) return null;
    const data = snap.data() as { siteContext?: { claims?: unknown } } | undefined;
    const raw = data?.siteContext?.claims;
    if (!Array.isArray(raw)) return [];
    // Normalise into the input shape; defensive against legacy claims
    // that lack new fields.
    const out: ImageObservationClaimInput[] = [];
    for (const c of raw) {
      if (!c || typeof c !== 'object') continue;
      const obj = c as Record<string, unknown>;
      const id = typeof obj.id === 'string' ? obj.id : '';
      const claim = typeof obj.claim === 'string' ? obj.claim : '';
      const sourceSpan = typeof obj.sourceSpan === 'string' ? obj.sourceSpan : '';
      const state = (obj.state === 'authored' || obj.state === 'mis-extracted')
        ? obj.state as 'authored' | 'mis-extracted'
        : undefined;
      const versionNote = typeof obj.versionNote === 'string' ? obj.versionNote : undefined;
      const lastWrittenAt = typeof obj.lastWrittenAt === 'string' ? obj.lastWrittenAt : undefined;
      const sourceDocId = typeof obj.sourceDocId === 'string' ? obj.sourceDocId : undefined;
      if (!id) continue;
      out.push({
        id, claim, sourceSpan,
        ...(state !== undefined ? { state } : {}),
        ...(versionNote !== undefined ? { versionNote } : {}),
        ...(lastWrittenAt !== undefined ? { lastWrittenAt } : {}),
        ...(sourceDocId !== undefined ? { sourceDocId } : {}),
      });
    }
    return out;
  } catch (e) {
    rethrowIfAborted(signal, e);
    console.warn('[imageObservationSentiment] firestore load failed:', (e as Error)?.message);
    return null;
  }
}

/* ── Mapper ────────────────────────────────────────────────────────── */

/**
 * Map one approved image-observation claim + a Gemini-scored polarity to
 * a `PublicSentimentEntry`. The `url` field uses a synthetic
 * `image-observation://` URI keyed by claim id — the orchestrator dedupes
 * by URL, and image-observations have no public URL (the source is a
 * user-uploaded image whose bytes are gone). The synthetic URI is stable
 * within a site so the same claim doesn't double-count across orchestrator
 * runs in the request-scoped cache.
 *
 * `confidence` is the IMAGE_OBSERVATION_CONFIDENCE_CAP regardless of text
 * length — the cap is the floor of the two compounding ceilings and the
 * cleanest single point of truth for "image-observation entries are
 * intrinsically lower-confidence than GDELT".
 */
export function mapImageObservationClaim(
  claim: ImageObservationClaimInput,
  sentimentScore: number,
): PublicSentimentEntry {
  const text = (claim.claim ?? claim.sourceSpan ?? '').toString();
  const btmTech = classifyBtmTech(text);
  // Strict Number.isFinite guard — a poisoned polarity score must never
  // propagate as a finite sentimentScore. NaN → 0 (neutral).
  const safeScore = Number.isFinite(sentimentScore)
    ? Math.max(-1, Math.min(1, sentimentScore))
    : 0;
  const fetchedAt = new Date().toISOString();
  // Synthetic URL — stable per (siteId, claimId). The orchestrator's
  // dedup-by-URL collapses re-runs cleanly; the audit log keeps the
  // sourceDocId via the supportingSnippet (the IIA-3 free-text is
  // already attribution-rich).
  const url = `image-observation://${encodeURIComponent(claim.id)}`;
  return {
    sourceId: 'image-observation',
    fetchedAt,
    btmTech,
    sentimentScore: safeScore,
    confidence: Number.isFinite(sentimentScore) ? IMAGE_OBSERVATION_CONFIDENCE_CAP : 0,
    supportingSnippet: truncateSnippet(text),
    url,
  };
}

/* ── Public entry point ────────────────────────────────────────────── */

export interface FetchImageObservationSentimentOptions {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** Test seam — inject a synthetic claim list to bypass Firestore. */
  claims?: readonly ImageObservationClaimInput[];
  /** Test seam — inject a stubbed Gemini runner. */
  runner?: typeof runGeminiJSON;
  /** Override the model. Default: gemini-2.5-flash. */
  model?: string;
  /** Override the per-call observation cap. Default: 200. */
  maxObservations?: number;
}

/**
 * PSR-FOLLOWUP-IIA-INGEST entry point — structurally satisfies
 * `PublicSentimentSourceFn` once wrapped in a closure that supplies
 * `siteId` (the orchestrator dispatches sources with `(queries[])` only;
 * the wrapper lives in `publicSentimentOrchestrator.ts`).
 *
 * Lifecycle:
 *  1. Resolve eligible claims — from `options.claims` when injected
 *     (unit-test seam), otherwise via Firestore at
 *     `users/{uid}/sites/{siteId}`.
 *  2. Filter to `versionNote === IIA_OBSERVATION_VERSION_NOTE` AND
 *     `state === 'authored'`.
 *  3. Empty after filter → `{ status: 'empty', entries: [] }` (NOT a
 *     thrown error — the orchestrator handles 'empty' identically to
 *     'ok' with zero entries).
 *  4. Cap to `MAX_OBSERVATIONS_PER_CALL` (default 200) — the orchestrator's
 *     50/200 cap is a separate concern (it caps the TOTAL across sources).
 *  5. Send one structured Gemini call for polarity classification.
 *  6. Map each (claim, polarity) → `PublicSentimentEntry` and return
 *     `{ status: 'ok', entries }`.
 *
 * NEVER throws.
 *
 * Return-shape extension: in addition to the existing
 * `PublicSentimentSourceResult` shapes, we emit `{ status: 'empty',
 * entries: [] }` when there are zero eligible claims. The orchestrator's
 * status-rollup logic (see `successesBySource` in `dispatchSourceTech`)
 * treats anything that's not `unavailable` as a clean success, so
 * 'empty' rolls up correctly without orchestrator changes.
 */
export async function fetchImageObservationSentiment(
  siteId: string,
  options: FetchImageObservationSentimentOptions = {},
): Promise<PublicSentimentSourceResult | { status: 'empty'; entries: PublicSentimentEntry[] }> {
  throwIfAborted(options.signal);
  if (!siteId || typeof siteId !== 'string') {
    return { status: 'unavailable', reason: 'siteId required', entries: [] };
  }
  // 1. Resolve claims (injection wins; Firestore is the production path).
  let rawClaims: ImageObservationClaimInput[] | null;
  if (options.claims !== undefined) {
    rawClaims = options.claims.slice();
  } else {
    rawClaims = await loadSiteClaimsForSentiment(siteId, options.signal);
  }
  throwIfAborted(options.signal);
  if (rawClaims === null) {
    // Firestore load failed (network blip / unauthenticated). Distinct
    // from "site has no claims" — the former is an error, the latter
    // is 'empty'.
    return { status: 'unavailable', reason: 'siteContext load failed', entries: [] };
  }

  // 2. Filter.
  const eligible = filterEligibleImageObservations(rawClaims);
  if (eligible.length === 0) {
    return { status: 'empty', entries: [] };
  }

  // 3. Cap inner-call payload size.
  const cap = options.maxObservations ?? MAX_OBSERVATIONS_PER_CALL;
  const capped = eligible.slice(0, cap);

  // 4. Polarity classify in one structured call.
  const polarityOptions: {
    runner?: typeof runGeminiJSON;
    model?: string;
    signal?: AbortSignal;
  } = {};
  if (options.runner !== undefined) polarityOptions.runner = options.runner;
  if (options.model !== undefined) polarityOptions.model = options.model;
  if (options.signal !== undefined) polarityOptions.signal = options.signal;
  const polarities = await classifyImageObservationPolarities(capped, polarityOptions);
  throwIfAborted(options.signal);

  // 5. Map. Unscored claims fall back to neutral 0 — they still
  //    contribute to the count (so an image-observation about wind-bess
  //    doesn't get silently dropped just because the classifier
  //    timed out on that row) but their score is honest about the
  //    missing signal.
  const entries: PublicSentimentEntry[] = capped.map(c => {
    const score = polarities.get(c.id);
    return mapImageObservationClaim(c, score ?? NaN);
  });

  return { status: 'ok', entries };
}
